import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import { NavLink } from 'react-router'
import styled, { css } from 'styled-components'

import { redirectToAuthEvent, userAtom } from '@root/entities/auth'
import { UserAvatar } from '@root/entities/auth/ui'
import { logoutDialogAtom } from '@root/features/auth/logout'
import { ShortcutId } from '@root/features/shortcut/shortcut-id'
import { PossibleShortcut } from '@root/features/shortcut/ui/possible-shortcut'
import { useTranslation } from '@root/shared/localization'
import { createIcon, LogoutIcon } from '@root/shared/ui-kit'

import { navigationExpandedAtom } from '../atoms'

import type { ClassNameProps } from '@root/shared/react'

type NavigationAccountProps = {} & ClassNameProps

export const NavigationAccount: React.FC<NavigationAccountProps> = ({
  className,
}) => {
  const [expanded] = useAtom(navigationExpandedAtom)

  const [user] = useAtom(userAtom)
  const { t } = useTranslation()

  const [, setLogoutModalOpen] = useAtom(logoutDialogAtom)

  const redirectToAuth = useAction(redirectToAuthEvent)

  const handleLoginClick = () => redirectToAuth()
  const handleLogoutClick = () => setLogoutModalOpen(true)

  if (!user) {
    return (
      <Root
        type="button"
        className={className}
        onClick={handleLoginClick}
        aria-expanded={expanded}
      >
        <LoginIconContainer>
          <LoginIcon width={8} height={10} />
        </LoginIconContainer>
        <Label aria-expanded={expanded}>{t('navigation.login')}</Label>
      </Root>
    )
  }

  return (
    <AccountRoot className={className} aria-expanded={expanded}>
      <PossibleShortcut shortcutId={ShortcutId.Profile}>
        <AccountLink as={NavLink} to={'profile'} aria-expanded={expanded}>
          <UserAvatar size={22} withHypedLetter outlineSpacingWidth={0.5} />
          <Label aria-expanded={expanded} $withIcon>
            <Username>{user.username}</Username>
          </Label>
        </AccountLink>
      </PossibleShortcut>
      <LogoutButton
        type="button"
        onClick={handleLogoutClick}
        aria-expanded={expanded}
      >
        <LogoutIcon size={18} />
      </LogoutButton>
    </AccountRoot>
  )
}

const Username = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Label = styled.div<{ $withIcon?: boolean }>`
  text-align: start;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-inline-start: 15px;

  opacity: 0;
  transition:
    opacity 0.1s ease-in-out,
    color 0.15s linear;

  ${({ $withIcon }) =>
    $withIcon &&
    css`
      padding-inline-end: 26px;
    `}

  &[aria-expanded='true'] {
    opacity: 1;
  }
`

const AccountRoot = styled.div`
  position: relative;
  height: 44px;
  width: 100%;
`

const Root = styled.button`
  all: unset;
  display: grid;
  justify-content: center;
  justify-items: center;
  align-items: center;
  padding-inline-start: 16.5px;
  height: 44px;

  transition: color 0.15s linear;
  cursor: pointer;
  text-decoration: none;
  grid-template-columns: auto 1fr;

  & ${Label} {
    color: var(--text-9);
  }
  &:hover {
    & ${Label} {
      color: var(--text-11);
    }
  }
  &.active {
    background: var(--backgroundA-2);
    & ${Label} {
      color: var(--text-12);
    }
  }
  &[aria-expanded='true'] {
    &:hover {
      background: var(--backgroundA-2);
    }
  }
`

const AccountLink = styled(Root)`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
`

const LogoutButton = styled.button`
  all: unset;
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 1;
  display: grid;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-9);
  opacity: 0;
  pointer-events: none;
  transition: color 0.15s linear;

  &[aria-expanded='true'] {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    color: var(--error-9);
  }
`

const LoginIconContainer = styled.div`
  width: 20px;
  height: 20px;
  background: var(--primary-9);
  color: var(--primary-contrast);
  border-radius: 50%;

  display: grid;
  align-items: center;
  justify-content: center;
`

// Keeping it here because it's ugly
const LoginIcon = createIcon(
  <>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 1H7V9H1V8.5H0V9C0 9.55229 0.447715 10 1 10H7C7.55228 10 8 9.55228 8 9V1C8 0.447715 7.55228 0 7 0H1C0.447715 0 0 0.447715 0 1V1.5H1V1Z"
      fill="currentColor"
    />
    <circle cx="0.5" cy="1.5" r="0.5" fill="currentColor" />
    <circle cx="0.5" cy="8.5" r="0.5" fill="currentColor" />
    <rect
      x="3.75"
      y="2.74219"
      width="3.18205"
      height="1.06068"
      rx="0.530341"
      transform="rotate(45 3.75 2.74219)"
      fill="currentColor"
    />
    <rect
      x="6"
      y="4.99219"
      width="3.18205"
      height="1.06068"
      rx="0.530341"
      transform="rotate(135 6 4.99219)"
      fill="currentColor"
    />
    <path d="M5 5H1" stroke="white" strokeLinecap="round" />
  </>,
  '0 0 8 10',
)
